import React, { useState, useRef, useEffect } from "react";
import CalculatorTemplate from "../../components/calculators/CalculatorTemplate";
import CalculatorPanel from "../../components/calculators/CalculatorPanel";
import CalculatorGenderSelector from "../../components/calculators/CalculatorGenderSelector";
import HeightInputGroup from "../../components/calculators/HeightInputGroup";
import CalculatorResults from "../../components/calculators/CalculatorResults";
import { calculateIdealBodyWeight } from "../../utils/calculators/ibw";
import { roundOne } from "../../utils/calculators/general";
import { isNumber } from "../../utils/isNumber";
import { getHeightInCm } from "../../utils/lengthConverter";
import { validateHeightFromInputs } from "../../utils/calculators/validation";
import { useHeightInput } from "../../hooks/calculatorHooks";
import useErrorFocus from "../../hooks/useErrorFocus";
import ibwMaleImg from "./images/etg_abg15_appendix_figure6_male-ideal-body-weight.png";
import ibwFemaleImg from "./images/etg_abg15_appendix_figure7_female-ideal-body-weight.png";
import styles from "../../components/calculators/calculator.module.scss";
import { forceDropdownProp } from "./forceDropdownProp";

const IbwCalcApp = (props) => {
  const { isWebLayout } = props;
  const [sex, setSex] = useState("");
  const [result, setResult] = useState("");
  const [errors, setErrors] = useState({});
  const [showResult, setShowResult] = useState(false);
  const resultsRef = useRef(null);
  const [forceDropdown, setForceDropdown] = React.useState(false);

  useEffect(() => {
    setForceDropdown(forceDropdownProp(props));
  }, [props]);

  const fieldOrder = ["sex", "height"];
  useErrorFocus(errors, fieldOrder);

  const {
    height,
    heightUnit,
    feet,
    inches,
    handleHeightChange,
    handleFeetChange,
    handleInchesChange,
    handleUnitChange,
    clearHeightFields,
    unitOptions: heightUnits,
  } = useHeightInput("cm");

  function validateFields() {
    let fieldErrors = {};
    const heightError = validateHeightFromInputs(
      heightUnit,
      height,
      feet,
      inches,
      true, // Use normal constants instead of relaxed
    );
    if (heightError) {
      fieldErrors.height = heightError;
    }
    if (!sex) fieldErrors.sex = "Please select a sex";
    return fieldErrors;
  }

  function handleClear() {
    setSex("");
    clearHeightFields();
    setResult("");
    setErrors({});
    setShowResult(false);
  }

  function handleCalculate(e) {
    e.preventDefault();
    const fieldErrors = validateFields();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setShowResult(false);
      setResult("");
      return;
    }

    const heightInCm = getHeightInCm(heightUnit, height, feet, inches);
    const value = calculateIdealBodyWeight({
      sex,
      height: heightInCm,
      heightUnit: "cm",
    });
    if (isNumber(value)) {
      const rounded = roundOne(value);
      setResult(
        Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1),
      );
    } else {
      setResult("");
    }
    setShowResult(true);
    setErrors({});
    setTimeout(() => {
      if (
        resultsRef.current &&
        typeof resultsRef.current.scrollIntoView === "function"
      ) {
        resultsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  }

  const formulaBlock = (
    <div className={isWebLayout ? "" : styles.formulaBlock}>
      <img
        src={ibwMaleImg}
        alt="IBW formula for males"
        className={isWebLayout ? "" : styles.formulaImage}
      />
      <img
        src={ibwFemaleImg}
        alt="IBW formula for females"
        className={isWebLayout ? "" : styles.formulaImage}
      />
    </div>
  );

  const description = (
    <div className={isWebLayout ? "" : styles.infoBlock}>
      This calculator is based on the formula:
    </div>
  );

  const panel = (
    <CalculatorPanel header="Calculator" onClear={handleClear} errors={{}}>
      <CalculatorGenderSelector
        value={sex}
        onChange={(value) => {
          setSex(value);
          setErrors((prev) => ({ ...prev, sex: "" }));
        }}
        error={errors.sex}
      />
      <HeightInputGroup
        idPrefix="ibw"
        height={height}
        heightUnit={heightUnit}
        feet={feet}
        inches={inches}
        onHeightChange={(e) => handleHeightChange(e, setErrors)}
        onFeetChange={(e) => handleFeetChange(e, setErrors)}
        onInchesChange={(e) => handleInchesChange(e, setErrors)}
        onUnitChange={(e) => handleUnitChange(e, setErrors)}
        error={errors.height}
        unitOptions={heightUnits}
        isWebLayout={isWebLayout}
        forceDropdown={forceDropdown}
      />
      <div className={styles.calcBtnRow}>
        <button
          className={styles.calcBtn}
          type="submit"
          onClick={handleCalculate}
        >
          Calculate
        </button>
      </div>
    </CalculatorPanel>
  );

  const results =
    showResult && result !== "" ? (
      <CalculatorResults
        label="Ideal body weight"
        value={result}
        unit={result ? "kg" : undefined}
        showResult={true}
      />
    ) : null;

  const calculatorTitle = "Ideal body weight calculator";

  return (
    <CalculatorTemplate
      title={calculatorTitle}
      description={description}
      formulaSection={formulaBlock}
      panel={panel}
      results={<div ref={resultsRef}>{results}</div>}
      isWebLayout={isWebLayout}
    />
  );
};

export default IbwCalcApp;
